import { useState, useEffect, useCallback } from 'react';
import { Eye, Check, Loader2 } from 'lucide-react';
import SmernicPreviewModal from '../../../components/SmernicPreviewModal';
import Dropdown from '../../../components/receptura/Dropdown';
import { TYRKYS, csrf, datum, Nadpis, Zpet, Posun } from './ui';

// Moje směrnice v Cockpitu: jen přiřazené (BOZP pro všechny). Klepnutí na řádek
// ho rozbalí — Číst otevře dokument, Potvrzuji zapíše seznámení s verzí.

export default function SmerniceSeznam({ isDark, user, otevritId, onZpet, onPotvrzeno }) {
    const [smernice, setSmernice] = useState(null);
    const [kategorie, setKategorie] = useState('');
    const [rozbalena, setRozbalena] = useState(otevritId || null);
    const [nahled, setNahled] = useState(null);
    const [potvrzuji, setPotvrzuji] = useState(null);

    const nacti = useCallback(() => fetch('/api/me/dokumentace/smernice').then(r => r.ok ? r.json() : []).then(data => {
        setSmernice(data);
        return data;
    }).catch(() => setSmernice([])), []);

    // Proklik z notifikace nebo z hledání rovnou otevře dokument
    useEffect(() => {
        nacti().then(data => {
            const cil = otevritId && (data || []).find(s => s.id === otevritId);
            if (cil?.verze) setNahled(cil);
        });
    }, [nacti, otevritId]);

    const potvrdit = async (id) => {
        setPotvrzuji(id);
        const r = await fetch(`/api/bezpecnost/smernice/${id}/potvrdit`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf(), 'Content-Type': 'application/json' } });
        // 422 „Již potvrzeno" je z pohledu člověka taky hotovo
        if (r.ok || r.status === 422) {
            await nacti();
            onPotvrzeno?.();
        }
        setPotvrzuji(null);
    };

    if (!smernice) {
        return <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
    }

    const kategorieMoznosti = [...new Map(smernice.filter(s => s.kategorie).map(s => [s.kategorie.id, s.kategorie])).values()];
    const zobrazene = kategorie ? smernice.filter(s => s.kategorie?.id === kategorie) : smernice;
    const skupiny = zobrazene.reduce((acc, s) => {
        const k = s.kategorie?.nazev || 'Ostatní';
        (acc[k] ||= []).push(s);
        return acc;
    }, {});
    const cekajicich = smernice.filter(s => s.k_potvrzeni).length;

    return (
        <Posun>
            <Zpet label="Znalosti" onClick={onZpet} />
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', margin: '4px 0 2px' }}>Směrnice</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)', marginBottom: 12 }}>
                {smernice.length} {smernice.length === 1 ? 'dokument' : smernice.length < 5 ? 'dokumenty' : 'dokumentů'}
                {cekajicich > 0 && <> · <span style={{ color: '#FBBF24' }}>{cekajicich} k potvrzení</span></>}
            </p>

            {kategorieMoznosti.length > 1 && (
                <div style={{ maxWidth: 240 }}>
                    <Dropdown value={kategorie} isDark={isDark} accent={TYRKYS} height={36}
                        options={[{ value: '', label: 'Všechny kategorie' }, ...kategorieMoznosti.map(k => ({ value: k.id, label: k.nazev }))]}
                        onChange={v => setKategorie(v === '' ? '' : Number(v))} />
                </div>
            )}

            {smernice.length === 0 && (
                <p className="text-sm text-center" style={{ color: 'var(--text-muted)', marginTop: 32 }}>Zatím ti není přiřazená žádná směrnice.</p>
            )}

            {Object.entries(skupiny).map(([nazev, polozky]) => (
                <div key={nazev}>
                    <Nadpis>{nazev}</Nadpis>
                    {polozky.map(s => {
                        const otevrena = rozbalena === s.id;
                        const barva = s.k_potvrzeni ? '#f59e0b' : '#10b981';
                        return (
                            <div key={s.id} onClick={() => setRozbalena(otevrena ? null : s.id)}
                                style={{
                                    background: s.k_potvrzeni && otevrena ? 'rgba(245,158,11,0.06)' : (isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-raised)'),
                                    border: `0.5px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}`,
                                    borderLeft: `3px solid ${s.vyzaduje_podpis ? barva : 'var(--text-muted)'}`,
                                    borderRadius: '0 10px 10px 0', padding: '10px 12px', marginBottom: 6, cursor: 'pointer',
                                }}>
                                <div className="flex items-center justify-between gap-2">
                                    <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'var(--text-muted)' }}>{s.cislo_dokumentu}</span>
                                    {s.vyzaduje_podpis && (
                                        <span className="text-[10.5px] font-bold rounded-full" style={{ padding: '3px 8px', background: s.k_potvrzeni ? 'rgba(245,158,11,0.14)' : 'rgba(16,185,129,0.14)', color: s.k_potvrzeni ? '#FBBF24' : '#34D399' }}>
                                            {s.k_potvrzeni ? 'Potvrdit' : 'Potvrzeno'}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[13.5px]" style={{ color: 'var(--text-primary)', fontWeight: otevrena ? 600 : 500, marginTop: 2 }}>{s.nazev}</div>
                                {otevrena && (
                                    <div onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
                                        <p className="text-[11px]" style={{ color: 'var(--text-muted)', marginTop: 3 }}>
                                            {s.verze && <>Verze {s.verze.cislo_verze} · platí od {datum(s.verze.created_at)}</>}
                                            {s.pro_vsechny && ' · pro všechny'}
                                            {s.potvrzeno_at && ` · potvrzeno ${datum(s.potvrzeno_at)}`}
                                        </p>
                                        {s.popis && <p className="text-xs" style={{ color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.45 }}>{s.popis}</p>}
                                        <div className="flex gap-2 flex-wrap" style={{ marginTop: 10 }}>
                                            {s.verze && (
                                                <button onClick={() => setNahled(s)} className="flex items-center gap-1.5 text-[13px] font-bold rounded-full"
                                                    style={{ height: 36, padding: '0 16px', background: isDark ? 'rgba(255,255,255,0.09)' : 'var(--surface-elevated)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                                                    <Eye size={16} /> Číst
                                                </button>
                                            )}
                                            {s.k_potvrzeni && (
                                                <button onClick={() => potvrdit(s.id)} disabled={potvrzuji === s.id} className="flex items-center gap-1.5 text-[13px] font-bold rounded-full"
                                                    style={{ height: 36, padding: '0 16px', background: 'var(--brand)', color: '#1a1000', cursor: 'pointer', opacity: potvrzuji === s.id ? 0.6 : 1 }}>
                                                    {potvrzuji === s.id ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Potvrzuji seznámení
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}

            {nahled?.verze && (
                <SmernicPreviewModal smernice={nahled} verze={nahled.verze} user={user}
                    uzPodepsano={!nahled.k_potvrzeni}
                    onPotvrdit={async (id) => { await potvrdit(id); setNahled(prev => prev ? { ...prev, k_potvrzeni: false } : prev); }}
                    onClose={() => setNahled(null)} />
            )}
        </Posun>
    );
}
